import { loader } from 'graphql.macro';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { get, omitBy } from 'lodash';

import { useNotificationContext } from '../context/notification-context';
import { CompanyType } from '../definitions.d';

const UPDATE_COMPANY_MUTATION = loader('../graphql/updateCompany.graphql');
const COMPANIES_QUERY = loader('../graphql/companies.graphql');

export interface UpdateCompanyProps {
  buildingNumber?: string;
  cityUuid?: string;
  commercialName?: string;
  contract?: File;
  contractEndDate?: string;
  contractStartDate?: string;
  payDay: number;
  countryUuid?: string;
  echekExecutive?: string;
  hasApiConnection?: boolean;
  industrySegmentUuid?: string;
  isAutoRenew?: boolean;
  legalName?: string;
  mainStreet?: string;
  maxWithdrawalPercent?: number;
  maxWithdrawalsPerMonth?: number;
  waitingDays?: number;
  minMonths?: number;
  minWithdrawalAmount?: number;
  neighborhoodUuid?: string;
  onDemandApi?: boolean;
  onDemandApiAuthUrl?: string;
  onDemandApiInfoUrl?: string;
  onDemandApiUser?: string;
  onDemandApiPassword?: string;
  onDemandApiCompanyCode?: string;
  platformFee?: number;
  require2fa?: boolean;
  secondaryStreet?: string;
  serviceFee?: number;
  stateUuid?: string;
  taxId?: string;
  taxPercent?: number;
  transferFee?: number;
  uuid: string;
  zipCode?: string;
}

interface UpdateCompanyResponse {
  updateCompany: (values: UpdateCompanyProps) => void;
  loading?: boolean;
  error?: Error;
}

const useUpdateCompanyMutation = (): UpdateCompanyResponse => {
  const { t } = useTranslation();
  const { showNotification } = useNotificationContext();
  const navigate = useNavigate();

  const [mutation, { loading, error }] = useMutation(UPDATE_COMPANY_MUTATION, {
    onError: (e) => {
      showNotification(e.message, 'error');
    },
    onCompleted: () => {
      showNotification(t('Company updated successfully'), 'success');
      navigate('/companies');
    }
  });

  return {
    updateCompany: (values: UpdateCompanyProps): void => {
      mutation({
        variables: omitBy(
          values,
          (value) => typeof value === 'undefined' || value === null
        ),
        update(cache, { data }) {
          const updatedCompany: CompanyType = get(
            data,
            'updateCompany.company'
          );
          let cachedValues;
          try {
            cachedValues = cache.readQuery({ query: COMPANIES_QUERY });
          } catch {
            cachedValues = {};
          }
          const cachedCompanies: CompanyType[] = get(
            cachedValues,
            'companies',
            []
          );
          if (cachedCompanies) {
            cache.writeQuery({
              query: COMPANIES_QUERY,
              data: {
                companies: cachedCompanies.map((company) =>
                  company.uuid === updatedCompany.uuid
                    ? updatedCompany
                    : company
                )
              }
            });
          }
        }
      });
    },
    loading,
    error
  };
};

export default useUpdateCompanyMutation;
